import { API_BASE as BASE_URL, getAuthHeaders, apiFetch } from './api-fetch';
import { TransferSigning } from '../types/transfer-signing';

const SIGNINGS_PATH = '/signings';

/**
 * Normalize a raw backend DynamoDB item → TransferSigning shape.
 * Backend stores whatever fields we POST; this maps flat fields to the
 * typed shape the frontend expects.
 */
function normalizeItem(item: any): TransferSigning {
  const buyerSigner = item.signers?.find((s: any) => s.type === 'buyer');
  const sellerSigner = item.signers?.find((s: any) => s.type === 'seller');

  const signers = item.signers ?? [
    ...(item.buyerName ? [{
      id: 'buyer',
      type: 'buyer' as const,
      name: item.buyerName || '',
      email: item.buyerEmail || '',
      phone: item.buyerPhone || '',
      confirmed: false,
    }] : []),
    ...(item.sellerName ? [{
      id: 'seller',
      type: 'seller' as const,
      name: item.sellerName || '',
      email: item.sellerEmail || '',
      phone: item.sellerPhone || '',
      confirmed: false,
    }] : []),
  ];

  return {
    id: item.id || '',
    dealId: item.dealId || item.id || '',
    dealTitle: item.title || item.dealTitle || 'Untitled Signing',
    propertyAddress: item.propertyAddress || '',
    status: item.status || 'scheduled',
    scheduledDate: item.scheduledDate || '',
    scheduledTime: item.scheduledTime || '',
    duration: item.duration || 60,
    location: item.location || 'Conveyancer Office',
    locationAddress: item.locationAddress || '',
    signers,
    agentId: item.agentId || '',
    agentName: item.agentName || '',
    conveyancerName: item.conveyancerName || '',
    conveyancerContact: item.conveyancerPhone || item.conveyancerContact || '',
    conveyancerEmail: item.conveyancerEmail || '',
    documentChecklist: item.documentChecklist || [],
    reminderSent: item.reminderSent || false,
    confirmationSent: item.confirmationSent || false,
    notes: item.notes || '',
    specialInstructions: item.specialInstructions || '',
    requiredDocuments: item.requiredDocuments || [],
    createdBy: item.createdBy || '',
    createdAt: typeof item.createdAt === 'number'
      ? new Date(item.createdAt).toISOString()
      : item.createdAt || new Date().toISOString(),
    updatedAt: typeof item.updatedAt === 'number'
      ? new Date(item.updatedAt).toISOString()
      : item.updatedAt || new Date().toISOString(),
    completedAt: item.completedAt,
    cancelledAt: item.cancelledAt,
    cancelledReason: item.cancelledReason,
    history: item.history || [],
  };
}

// Get all signings — backend returns { data: { items: [...] } }
export async function getSignings(): Promise<TransferSigning[]> {
  try {
    const response = await apiFetch(`${BASE_URL}${SIGNINGS_PATH}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch signings');
    }
    const data = await response.json();
    const raw: any[] = data.data?.items ?? data.data?.signings ?? data.items ?? [];
    return raw.map(normalizeItem);
  } catch (error) {
    console.error('Error fetching signings:', error);
    throw error;
  }
}

// Get single signing — backend returns { data: { item: {...} } }
export async function getSigning(id: string): Promise<TransferSigning> {
  try {
    const response = await apiFetch(`${BASE_URL}${SIGNINGS_PATH}/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch signing');
    }
    const data = await response.json();
    return normalizeItem(data.data?.item ?? data.data ?? {});
  } catch (error) {
    console.error('Error fetching signing:', error);
    throw error;
  }
}

// Create signing — backend returns { data: { item: {...} } } with 201
export async function createSigning(signingData: Partial<TransferSigning> & { title?: string }): Promise<TransferSigning> {
  try {
    const response = await apiFetch(`${BASE_URL}${SIGNINGS_PATH}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(signingData),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to create signing');
    }
    const data = await response.json();
    return normalizeItem(data.data?.item ?? data.data ?? signingData);
  } catch (error) {
    console.error('Error creating signing:', error);
    throw error;
  }
}

// Update signing — backend returns { data: { success: true } } (no item)
// Returns the locally-merged signing for optimistic update.
export async function updateSigning(id: string, updates: Partial<TransferSigning>): Promise<void> {
  try {
    const response = await apiFetch(`${BASE_URL}${SIGNINGS_PATH}/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update signing');
    }
  } catch (error) {
    console.error('Error updating signing:', error);
    throw error;
  }
}

// Delete signing
export async function deleteSigning(id: string): Promise<void> {
  try {
    const response = await apiFetch(`${BASE_URL}${SIGNINGS_PATH}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to delete signing');
    }
  } catch (error) {
    console.error('Error deleting signing:', error);
    throw error;
  }
}

// Add history entry — POST /signings/{id}/history
export async function addSigningHistory(signingId: string, historyData: any): Promise<void> {
  try {
    const response = await apiFetch(`${BASE_URL}${SIGNINGS_PATH}/${signingId}/history`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(historyData),
    });
    if (!response.ok) {
      console.warn('History entry failed (non-critical):', response.status);
    }
  } catch (error) {
    console.warn('Error adding history entry (non-critical):', error);
  }
}

export const signingAPI = {
  getSignings,
  getSigning,
  createSigning,
  updateSigning,
  deleteSigning,
  addSigningHistory,
};
